import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import { API, axios } from "@/api";
import { BrandLogo } from "@/components/shared/BrandLogo";
import { UnifiedStatusBadge } from "@/components/shared/UnifiedStatusBadge";
import { useUnifiedStatus } from "@/contexts/UnifiedStatusContext";
import { getCategoryColor } from "@/lib/categoryColors";
import {
  Sparkles, Clock, Download, CheckCircle, Gift, ChevronRight,
  ChevronDown, Calendar, Star, Zap, PartyPopper, Rocket, Target,
  Home, Loader2, Plus,
} from "lucide-react";
import { toast } from "sonner";
import { BirthdayCelebration } from "@/components/shared/BirthdayCelebration";
import { trackPageView } from "@/lib/track";

// Lighten/darken a hex color
const adjustColor = (hex, amount) => {
  const num = parseInt(hex.replace("#", ""), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + amount));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0xff) + amount));
  const b = Math.min(255, Math.max(0, (num & 0xff) + amount));
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
};

const PHASE_MSG = {
  10: "10 Days — Start Prepping!",
  9: "9 Days to Go!",
  8: "8 Days — Getting Close!",
  7: "One Week to Go!",
  6: "6 Days!",
  5: "5 Days — Almost!",
  4: "Final Stretch!",
  3: "3 Days!",
  2: "2 Days — So Close!",
  1: "TOMORROW!",
  0: "HAPPY BIRTHDAY!",
};

const SECTIONS = [
  { key: "apps", label: "Download Apps Now", icon: Download, emptyMsg: "No app downloads needed!" },
  { key: "birthday", label: "Birthday-Only Deals", icon: Star, emptyMsg: "No birthday-only deals found" },
  { key: "easy", label: "Easy Wins — No App", icon: Zap, emptyMsg: "No easy wins available" },
  { key: "top", label: "Highest Value Deals", icon: Gift, emptyMsg: "Loading..." },
];

const DealRow = ({ offer, onClick, isTracked, onAddToPlan, showDownload, dealStatus }) => {
  const cc = getCategoryColor(offer.category);
  return (
  <motion.div
    initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
    className={`flex items-center gap-3 py-2.5 px-3 rounded-xl ${cc.light} border ${cc.border} transition-colors`}
  >
    <div className="flex-1 flex items-center gap-3 min-w-0 cursor-pointer" onClick={onClick}>
      <BrandLogo name={offer.name} logoUrl={offer.logo_url} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <h4 className="text-sm font-semibold text-slate-800 truncate">{offer.name}</h4>
          {dealStatus && <UnifiedStatusBadge status={dealStatus} />}
        </div>
        <p className={`text-xs ${cc.text} font-medium truncate`}>{offer.category} · {offer.offer_title}</p>
      </div>
      <div className="text-right flex-shrink-0">
        <span className="text-sm font-bold text-green-600">${offer.offer_value_est}</span>
        {offer.offer_window_days <= 1 && <p className="text-[10px] text-red-500 font-semibold">1-day only</p>}
      </div>
    </div>
    {showDownload && offer.website && (
      <a
        href={offer.website.startsWith("http") ? offer.website : `https://${offer.website}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-100 hover:bg-indigo-200 flex items-center justify-center transition-colors"
        data-testid={`dl-${offer.business_id}`}
        title={`Download ${offer.name} app`}
      >
        <Download className="w-4 h-4 text-indigo-600" />
      </a>
    )}
    {isTracked ? (
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 flex items-center justify-center" data-testid={`planned-${offer.business_id}`}>
        <CheckCircle className="w-4 h-4 text-green-500" />
      </span>
    ) : (
      <button
        onClick={(e) => { e.stopPropagation(); onAddToPlan(offer.business_id); }}
        className="flex-shrink-0 w-8 h-8 rounded-full bg-sky-100 hover:bg-sky-200 flex items-center justify-center transition-colors"
        data-testid={`add-plan-${offer.business_id}`}
      >
        <Plus className="w-4 h-4 text-sky-600" />
      </button>
    )}
  </motion.div>
  );
};

export default function CountdownPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { getStatus } = useUnifiedStatus();
  const [stats, setStats] = useState(null);
  const [offers, setOffers] = useState([]);
  const [tracker, setTracker] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openSection, setOpenSection] = useState(null);

  const userColor = user?.favorite_color || "#0ea5e9";
  const colorLight = adjustColor(userColor, 60);
  const colorDark = adjustColor(userColor, -40);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, offersRes, trackerRes, readinessRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/offers?limit=500`),
        axios.get(`${API}/tracker`),
        axios.get(`${API}/user/readiness-score`),
      ]);
      setStats(statsRes.data);
      setOffers(offersRes.data);
      setTracker(trackerRes.data);
      setReadiness(readinessRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { trackPageView("countdown"); }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 animate-spin" style={{ color: userColor }} /></div>;

  const days = stats?.days_until_birthday ?? 999;

  // If birthday is more than 10 days away, redirect home
  if (days > 10) { navigate("/", { replace: true }); return null; }

  const trackerIds = new Set(tracker.map(t => t.business_id));
  const claimedIds = new Set(tracker.filter(t => t.claimed).map(t => t.business_id));

  // Categorize deals
  const sectionData = {
    apps: offers.filter(o => o.offer_requires_app && !trackerIds.has(o.business_id)).sort((a, b) => (b.offer_value_est || 0) - (a.offer_value_est || 0)),
    birthday: offers.filter(o => o.offer_window_days <= 1).sort((a, b) => (b.offer_value_est || 0) - (a.offer_value_est || 0)),
    easy: offers.filter(o => o.ease_stars >= 4 && !o.offer_requires_app).sort((a, b) => (b.offer_value_est || 0) - (a.offer_value_est || 0)).slice(0, 15),
    top: [...offers].sort((a, b) => (b.offer_value_est || 0) - (a.offer_value_est || 0)).slice(0, 15),
  };

  const totalValue = offers.reduce((s, o) => s + (o.offer_value_est || 0), 0);
  const trackedCount = offers.filter(o => trackerIds.has(o.business_id)).length;
  const claimedCount = offers.filter(o => claimedIds.has(o.business_id)).length;
  const phaseLbl = PHASE_MSG[Math.min(days, 10)] || `${days} Days`;

  const toggleSection = (key) => setOpenSection(openSection === key ? null : key);

  // Quick-add to plan (tracker) directly from countdown page
  const addToPlan = async (businessId) => {
    try {
      await axios.post(`${API}/tracker`, { business_id: businessId, signed_up: true });
      setTracker(prev => [...prev, { business_id: businessId, claimed: false }]);
      toast.success("Added to your birthday plan!");
    } catch (e) { console.error(e); }
  };

  // Section bar colors — soft, warm gradients
  const sectionColors = {
    apps: { bg: "#818cf8", text: "#fff" },
    birthday: { bg: "#fbbf24", text: "#fff" },
    easy: { bg: "#4ade80", text: "#fff" },
    top: { bg: "#fb7185", text: "#fff" },
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-6" data-testid="countdown-page">
      {/* ═══════ BIRTHDAY CELEBRATION ═══════ */}
      {days === 0 && <BirthdayCelebration name={user?.first_name} />}

      {/* ═══════ HERO ═══════ */}
      <div className="relative overflow-hidden p-6 pb-8" style={{ background: `linear-gradient(135deg, ${colorDark}, ${userColor}, ${colorLight})` }}>
        {/* Animated sparkle dots */}
        <div className="absolute inset-0 opacity-15">
          {[...Array(14)].map((_, i) => (
            <motion.div key={i} className="absolute w-1.5 h-1.5 bg-white rounded-full"
              style={{ left: `${(i * 19 + 3) % 100}%`, top: `${(i * 23 + 8) % 100}%` }}
              animate={{ opacity: [0.2, 1, 0.2], scale: [0.6, 1.3, 0.6] }}
              transition={{ duration: 2 + (i % 3), repeat: Infinity, delay: i * 0.25 }}
            />
          ))}
        </div>

        <div className="relative z-10">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {days <= 1 ? <PartyPopper className="w-5 h-5 text-white/80" /> : days <= 4 ? <Zap className="w-5 h-5 text-white/80" /> : <Rocket className="w-5 h-5 text-white/80" />}
                <span className="text-xs font-semibold text-white/80 uppercase tracking-wider">Birthday Countdown</span>
              </div>
              <p className="text-sm text-white/75 mt-1">{user?.first_name}'s birthday planner</p>
            </div>
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", damping: 8 }}
              className="text-right"
            >
              <p className="font-black text-white leading-none drop-shadow-lg" style={{ fontSize: `${Math.max(4, 8 - days * 0.35)}rem` }}>{days}</p>
              <p className="text-xs font-semibold text-white/60 mt-1">{days === 1 ? "day left" : days === 0 ? "TODAY!" : "days left"}</p>
            </motion.div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-2 mt-5">
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-white">${totalValue.toFixed(0)}</p>
              <p className="text-[10px] text-white/70 font-medium">Total Value</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-white">{trackedCount}</p>
              <p className="text-[10px] text-white/70 font-medium">Planned</p>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-white">{claimedCount}</p>
              <p className="text-[10px] text-white/70 font-medium">Claimed</p>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════ PROGRESS ═══════ */}
      <div className="px-4 -mt-4 relative z-10 flex flex-col gap-2">
        {/* Readiness Score Ring */}
        {readiness && (
          <div className="bg-white rounded-2xl shadow-md p-5 border border-slate-100" data-testid="readiness-card">
            <div className="flex items-center gap-5">
              {/* Circular Progress Ring */}
              <div className="relative flex-shrink-0" style={{ width: 80, height: 80 }}>
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="#e2e8f0" strokeWidth="6" />
                  <motion.circle
                    cx="40" cy="40" r="34" fill="none"
                    stroke={userColor}
                    strokeWidth="6"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 34}`}
                    initial={{ strokeDashoffset: 2 * Math.PI * 34 }}
                    animate={{ strokeDashoffset: 2 * Math.PI * 34 * (1 - readiness.score / 100) }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    transform="rotate(-90 40 40)"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-black" style={{ color: userColor }}>{readiness.score}%</span>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-slate-800 mb-1">Readiness Score</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{readiness.status}</p>
              </div>
            </div>

            {/* Component breakdown */}
            <div className="mt-4 space-y-1.5">
              {Object.entries(readiness.components).map(([key, comp]) => (
                <div key={key} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${comp.earned === comp.max ? "bg-emerald-100" : "bg-slate-100"}`}>
                    {comp.earned === comp.max
                      ? <CheckCircle className="w-3 h-3 text-emerald-500" />
                      : <span className="w-1.5 h-1.5 rounded-full bg-slate-300" />}
                  </div>
                  <span className={`text-xs flex-1 ${comp.earned === comp.max ? "text-slate-700" : "text-slate-400"}`}>{comp.label}</span>
                  <span className={`text-xs font-bold ${comp.earned === comp.max ? "text-emerald-600" : "text-slate-400"}`}>{comp.earned}/{comp.max}</span>
                </div>
              ))}
            </div>

            {/* Actionable checklist */}
            {readiness.checklist.length > 0 && (
              <div className="mt-3 pt-3 border-t border-slate-100">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">To Do</p>
                <div className="space-y-1">
                  {readiness.checklist.slice(0, 5).map((item, i) => (
                    <button
                      key={i}
                      onClick={() => navigate(item.action)}
                      className="w-full flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 transition text-left"
                      data-testid={`checklist-item-${i}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.priority === "high" ? "bg-red-400" : item.priority === "medium" ? "bg-amber-400" : "bg-slate-300"}`} />
                      <span className="text-xs text-slate-600 flex-1">{item.text}</span>
                      <ChevronRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
                    </button>
                  ))}
                  {readiness.checklist.length > 5 && (
                    <p className="text-[10px] text-slate-400 text-center pt-1">+{readiness.checklist.length - 5} more</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div
          className="bg-white rounded-2xl shadow-md p-4 border border-slate-100 cursor-pointer hover:border-sky-200 transition-colors"
          onClick={() => navigate("/tracker")}
          data-testid="countdown-progress"
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-bold text-slate-800">Your Birthday Plan</span>
            <span className="text-xs font-semibold flex items-center gap-1" style={{ color: userColor }}>
              {trackedCount} of {offers.length} planned
              <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full rounded-full"
              style={{ background: `linear-gradient(90deg, ${userColor}, ${colorLight})` }}
              initial={{ width: 0 }}
              animate={{ width: `${offers.length > 0 ? Math.round((trackedCount / offers.length) * 100) : 0}%` }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </div>

        {/* ═══════ ACCORDION SECTIONS ═══════ */}
        {SECTIONS.map(({ key, label, icon: Icon, emptyMsg }) => {
          const items = sectionData[key] || [];
          const isOpen = openSection === key;
          const colors = sectionColors[key];

          return (
            <div key={key} className="rounded-2xl overflow-hidden shadow-md" data-testid={`section-${key}`}>
              {/* Bar */}
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center gap-3 px-4 py-3.5 transition-all"
                style={{ backgroundColor: colors.bg, color: colors.text }}
                data-testid={`section-bar-${key}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm font-bold flex-1 text-left">{label}</span>
                <span className="text-xs font-semibold bg-white/20 px-2.5 py-0.5 rounded-full">{items.length}</span>
                <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
                  <ChevronDown className="w-5 h-5" />
                </motion.div>
              </button>

              {/* Expandable content */}
              <AnimatePresence>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeInOut" }}
                    className="bg-white overflow-hidden"
                  >
                    <div className="p-3 max-h-[300px] overflow-y-auto">
                      {items.length === 0 ? (
                        <p className="text-sm text-slate-400 text-center py-4">{emptyMsg}</p>
                      ) : (
                        <div className="space-y-0.5">
                          {items.map(o => (
                            <DealRow key={o.business_id} offer={o} onClick={() => navigate(`/offer/${o.business_id}`)} isTracked={trackerIds.has(o.business_id)} onAddToPlan={addToPlan} showDownload={key === "apps"} dealStatus={getStatus(o.business_id)} />
                          ))}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* ═══════ GO TO HOME BUTTON ═══════ */}
        <motion.button
          onClick={() => { sessionStorage.setItem("countdown_dismissed", "1"); navigate("/"); }}
          whileTap={{ scale: 0.97 }}
          className="w-full py-4 rounded-2xl bg-white shadow-md border border-slate-100 flex items-center justify-center gap-2 text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors"
          data-testid="go-to-home-btn"
        >
          <Home className="w-5 h-5 text-slate-400" />
          Browse All Offers
        </motion.button>
      </div>
    </div>
  );
}
